import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, CalendarDays, IndianRupee, ShieldCheck, XCircle, CheckCircle2, Ban } from "lucide-react";
import api, { formatApiErrorDetail, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import BookingRatingBlock from "@/components/locanest/BookingRatingBlock";
import ReportProblemDialog from "@/components/locanest/ReportProblemDialog";
import SignAgreementDialog from "@/components/locanest/SignAgreementDialog";
import ProtectedPayButton, { PURPOSE_LABEL } from "@/components/locanest/ProtectedPayButton";
import { PenLine, HandCoins, Landmark, FileText, Upload } from "lucide-react";
import { uploadMedia } from "@/lib/upload";

const STATUS_META = {
  pending: { label: "Payment pending", color: "text-[#C85A32]" },
  advance_paid: { label: "Advance paid · Reserved", color: "text-[#2B3A67]" },
  completed: { label: "Completed", color: "text-[#2B3A67]" },
  rejected: { label: "Rejected by owner (refunded)", color: "text-red-600" },
  cancelled: { label: "Cancelled by tenant", color: "text-red-600" },
};

// Free-launch bookings (advance_enforced=false) have no payment; relabel accordingly.
function statusMetaFor(b) {
  if (b?.kind === "protected_payment") {
    if (b.status === "pending") return { label: "Payment not completed", color: "text-[#C85A32]" };
    if (b.status === "advance_paid") return { label: "Paid · Held by LocaNest", color: "text-[#2B3A67]" };
  }
  if (b?.status === "pending" && b?.advance_enforced === false) {
    return { label: "Awaiting owner", color: "text-[#2B3A67]" };
  }
  return STATUS_META[b?.status] || { label: b?.status, color: "text-[#5C6B7A]" };
}

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [prop, setProp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [agrPct, setAgrPct] = useState(null);
  const uploadAgreement = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("Agreement file must be under 10MB");
    try {
      setAgrPct(0);
      const res = await uploadMedia(f, "doc", setAgrPct);
      await api.post(`/bookings/${id}/agreement-doc`, { url: res.url, name: res.name, size: res.size, content_type: res.content_type });
      toast.success("Agreement paper attached — you can now mark the deal completed");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setAgrPct(null); }
  };

  const load = () => api.get(`/bookings/${id}`).then(({ data }) => setB(data)).catch(() => setB(false));
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (b?.property_id && b.kind !== "protected_payment") api.get(`/properties/${b.property_id}`).then(({ data }) => setProp(data)).catch(() => {});
  }, [b?.property_id, b?.kind]);

  if (b === false) return <div className="max-w-3xl mx-auto p-10 text-center">Booking not found.</div>;
  if (!b) return <div className="max-w-3xl mx-auto p-10 text-center text-[#5C6B7A]">Loading…</div>;

  const isOwner = user?.id === b.owner_id;
  const isTenant = user?.id === b.tenant_id;
  const meta = statusMetaFor(b);
  const isFreeLaunch = b.advance_enforced === false;
  const hasOrderId = !!(b.cashfree_order_id || b.razorpay_order_id);

  const doAction = async (path, body = {}) => {
    setBusy(true);
    try {
      await api.post(`/bookings/${id}/${path}`, body);
      toast.success("Done");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadAgreement = () => downloadPdf("agreement.pdf", `${b.agreement_number}.pdf`);
  const downloadReceipt = () => downloadPdf("receipt.pdf", `LocaNest-Receipt-${b.agreement_number}.pdf`);

  const downloadPdf = async (kind, filename) => {
    try {
      const res = await api.get(`/bookings/${id}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      // With responseType='blob', err.response.data is a Blob — parse it for JSON detail
      let detail = "Download failed";
      try {
        const text = await err.response?.data?.text?.();
        if (text) {
          try { detail = JSON.parse(text).detail || detail; }
          catch { detail = text; }
        }
      } catch {}
      toast.error(detail);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-10">
      <Link to={isOwner ? "/owner" : "/bookings"} className="inline-flex items-center text-sm text-[#5C6B7A] hover:text-[#C85A32] mb-4">
        <ArrowLeft size={14} className="mr-1" /> Back
      </Link>

      <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Reservation</div>
      <h1 className="font-display text-3xl md:text-4xl mt-2">{b.property_title}</h1>
      <div className={`mt-2 inline-flex items-center gap-2 text-sm font-medium ${meta.color}`} data-testid="booking-status">
        {b.status === "advance_paid" || b.status === "completed" ? <ShieldCheck size={16} /> : b.status === "rejected" ? <XCircle size={16} /> : b.status === "cancelled" ? <Ban size={16} /> : <CheckCircle2 size={16} />}
        {meta.label}
      </div>

      <div className="mt-6 bg-[#F0EBE1] rounded-lg border border-[#1A1F2C]/10 p-6 space-y-3">
        <Row k="Agreement no." v={b.agreement_number} />
        {b.kind === "protected_payment" && (
          <Row k="Type" v={<span className="text-[#2B3A67] font-semibold" data-testid="booking-kind-protected">Protected payment via LocaNest · {({ token: "Token / booking amount", deposit: "Security deposit", rent: "First month rent", other: "Agreed amount" })[b.payment_purpose] || b.payment_purpose}{b.status === "advance_paid" ? " · held by LocaNest, payout to owner in 3–4 days" : ""}</span>} />
        )}
        {b.tenant_note && <Row k="Tenant note" v={b.tenant_note} />}
        {isFreeLaunch && (b.amount_paise || 0) === 0 ? (
          <Row k="Payment" v={<span className="text-[#2B3A67] font-semibold">Free interest — no advance collected</span>} />
        ) : (
          <Row k="Amount charged" v={<span className="inline-flex items-center text-[#C85A32] font-semibold"><IndianRupee size={13} />{((b.amount_paise || 0) / 100).toLocaleString("en-IN")}</span>} />
        )}
        {b.check_in && <Row k="Check-in" v={<span className="inline-flex items-center gap-1"><CalendarDays size={13} /> {b.check_in}</span>} />}
        {b.check_out && <Row k="Check-out" v={<span className="inline-flex items-center gap-1"><CalendarDays size={13} /> {b.check_out}</span>} />}
        {b.nights && <Row k="Nights" v={b.nights} />}
        <Row k="Tenant" v={`${b.tenant_name} · ${b.tenant_email}`} />
        <Row k="Owner" v={b.owner_name} />
        {hasOrderId && <Row k={b.cashfree_order_id ? "Cashfree order" : "Razorpay order"} v={b.cashfree_order_id || b.razorpay_order_id} mono />}
        {b.razorpay_payment_id && <Row k="Payment id" v={b.razorpay_payment_id} mono />}
        {(b.cashfree_refund_id || b.razorpay_refund_id) && <Row k="Refund id" v={b.cashfree_refund_id || b.razorpay_refund_id} mono />}
        {b.notes && <Row k="Notes" v={b.notes} />}
      </div>

      {isTenant && b.deal_agreement_doc && (
        <div className="mt-4 text-sm flex items-center gap-2" data-testid="tenant-agreement-doc">
          <FileText size={15} className="text-[#2B3A67]" /> Owner&rsquo;s signed agreement paper: <a href={b.deal_agreement_doc.url} target="_blank" rel="noreferrer" className="underline text-[#C85A32]">{b.deal_agreement_doc.name || "View"}</a>
        </div>
      )}

      {b.kind === "protected_payment" && (b.status === "advance_paid" || b.status === "completed") && (
        <div className="mt-6 rounded-lg border border-[#2B3A67]/25 bg-[#2B3A67]/5 p-5 text-sm" data-testid="payment-confirmation">
          <div className="flex items-center gap-2 text-[#2B3A67] font-semibold"><ShieldCheck size={16} /> {isOwner ? "Payment received for you" : "Payment received by LocaNest"}</div>
          {isOwner ? (
            <p className="mt-2 text-[#1A1F2C]">
              <strong>{b.tenant_name}</strong> has paid <strong>₹{((b.amount_paise || 0) / 100).toLocaleString("en-IN")}</strong> for <strong>{b.property_title}</strong> through LocaNest.
              {b.payout_status === "paid"
                ? <> LocaNest has <strong>transferred</strong> this amount to your bank/UPI{b.payout_reference ? ` (ref ${b.payout_reference})` : ""}.</>
                : b.payout_status === "on_hold"
                  ? <> This payout is currently <strong>on hold</strong> while LocaNest reviews a reported issue.</>
                  : <> <strong>LocaNest will transfer this amount to your saved bank/UPI within 3–4 days.</strong></>}
            </p>
          ) : (
            <p className="mt-2 text-[#1A1F2C]">
              You paid <strong>₹{((b.amount_paise || 0) / 100).toLocaleString("en-IN")}</strong> ({PURPOSE_LABEL[b.payment_purpose] || "agreed amount"}) for <strong>{b.property_title}</strong> to <strong>{b.owner_name}</strong>.
              LocaNest has received it and will transfer it to the owner/broker within <strong>3–4 days</strong>, commission-free. {b.paid_at ? `Paid on ${new Date(b.paid_at).toLocaleString()}.` : ""}
            </p>
          )}
          {isOwner && !user?.bank?.payout_method && (
            <Link to="/profile?section=bank" className="mt-3 inline-flex items-center gap-1.5 text-[#C85A32] font-medium underline" data-testid="owner-add-bank-link"><Landmark size={14} /> Add your bank / UPI details so we can transfer</Link>
          )}
        </div>
      )}

      {isFreeLaunch && b.kind !== "protected_payment" && (b.payments || []).length > 0 && (
        <div className="mt-6 rounded-lg border border-[#2B3A67]/25 bg-[#2B3A67]/5 p-5 text-sm" data-testid="linked-payments">
          <div className="flex items-center gap-2 text-[#2B3A67] font-semibold"><HandCoins size={16} /> Payments via LocaNest for this deal</div>
          <ul className="mt-2 space-y-1.5">
            {b.payments.map((p) => (
              <li key={p.id} className="flex flex-wrap justify-between gap-2">
                <span>₹{((p.amount_paise || 0) / 100).toLocaleString("en-IN")} · {PURPOSE_LABEL[p.payment_purpose] || p.payment_purpose}</span>
                <Link to={`/bookings/${p.id}`} className="text-[#C85A32] underline">{p.payout_status === "paid" ? "Transferred to owner" : "Held by LocaNest · receipt"}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {b.status !== "cancelled" && (
        <div className="mt-6" data-testid="report-problem-wrap">
          <ReportProblemDialog booking={b} otherPartyName={isTenant ? b.owner_name : b.tenant_name} />
          {b.open_dispute_id && <div className="mt-2 text-xs text-red-700" data-testid="dispute-open-note">A report on this deal is open with LocaNest (#{b.open_dispute_id.slice(0, 8)}).</div>}
        </div>
      )}

      {(b.status === "advance_paid" || b.status === "completed") && (
        <div className="mt-6 flex flex-wrap gap-3">
          {b.amount_paise > 0 && (
            <Button onClick={downloadReceipt} className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="download-receipt">
              <Download size={14} className="mr-1.5" /> Download payment receipt
            </Button>
          )}
          <Button onClick={downloadAgreement} variant={b.kind === "protected_payment" ? "outline" : "default"} className={b.kind === "protected_payment" ? "rounded-md" : "bg-[#2B3A67] hover:bg-[#1f2c52] text-white rounded-md"} data-testid="download-agreement">
            <Download size={14} className="mr-1.5" /> Download agreement PDF
          </Button>
          {isTenant && (
            <SignAgreementDialog
              bookingId={b.id}
              tenantName={b.tenant_name}
              defaultAddress={b.tenant_full_address || ""}
              onSigned={load}
              trigger={
                <Button className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="open-sign-agreement">
                  <PenLine size={14} className="mr-1.5" /> {b.tenant_signed_at ? "Update signature" : "Sign agreement"}
                </Button>
              }
            />
          )}
          {isTenant && b.tenant_signed_at && (
            <div className="w-full text-xs text-[#2B3A67]" data-testid="signed-note">
              ✔ Signed on {new Date(b.tenant_signed_at).toLocaleString()} — the owner receives the personalised agreement.
            </div>
          )}
          {isTenant && b.status === "advance_paid" && (
            <Button variant="outline" onClick={() => window.confirm("Cancellation is non-refundable per LocaNest policy. Continue?") && doAction("cancel", { reason: "Tenant cancelled" })} disabled={busy} className="text-red-600 rounded-md" data-testid="tenant-cancel">
              <Ban size={14} className="mr-1.5" /> Cancel (non-refundable)
            </Button>
          )}
          {isOwner && b.status === "advance_paid" && (
            <>
              <div className="w-full rounded-md border border-[#2B3A67]/20 bg-[#2B3A67]/5 p-3 text-sm" data-testid="agreement-doc-block">
                <div className="font-semibold text-[#2B3A67] flex items-center gap-2"><FileText size={15} /> Signed agreement paper {b.deal_agreement_doc ? "✔ attached" : "— required to complete this deal"}</div>
                {b.deal_agreement_doc ? (
                  <div className="mt-1.5 flex items-center gap-3 text-xs">
                    <a href={b.deal_agreement_doc.url} target="_blank" rel="noreferrer" className="underline truncate" data-testid="agreement-doc-link">{b.deal_agreement_doc.name || "View agreement"}</a>
                    <label className="ml-auto cursor-pointer text-[#C85A32] underline">
                      Replace<input type="file" accept="application/pdf,image/*" className="hidden" onChange={uploadAgreement} disabled={busy} />
                    </label>
                  </div>
                ) : (
                  <label className="mt-2 inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-dashed border-[#2B3A67]/40 bg-white cursor-pointer hover:border-[#C85A32]" data-testid="upload-agreement-doc">
                    <Upload size={13} /> {agrPct !== null ? `Uploading… ${agrPct}%` : "Upload signed agreement (PDF or photo, ≤10MB)"}
                    <input type="file" accept="application/pdf,image/*" className="hidden" onChange={uploadAgreement} disabled={busy || agrPct !== null} />
                  </label>
                )}
              </div>
              <Button onClick={() => doAction("complete")} disabled={busy || !b.deal_agreement_doc} title={b.deal_agreement_doc ? "" : "Upload the signed agreement first"} className="bg-[#2B3A67] hover:bg-[#1f2c52] text-white rounded-md disabled:opacity-50" data-testid="owner-complete">
                <CheckCircle2 size={14} className="mr-1.5" /> Mark completed
              </Button>
              <Button variant="outline" onClick={() => { const r = window.prompt("Reason for rejecting (visible to tenant)"); if (r !== null) doAction("reject", { reason: r }); }} disabled={busy} className="text-red-600 rounded-md" data-testid="owner-reject">
                <XCircle size={14} className="mr-1.5" /> Reject (full refund)
              </Button>
            </>
          )}
        </div>
      )}
      {b.status === "pending" && isTenant && (
        isFreeLaunch ? (
          <div className="mt-6 space-y-3">
            <div className="rounded-md bg-[#D4DEC6]/30 border border-[#2B3A67]/20 p-4 text-sm text-[#1A1F2C]" data-testid="free-launch-note">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#2B3A67] font-semibold mb-1">Interest sent · free, no payment taken</div>
              The owner has been notified in-app and via email and will reach out shortly. You can also contact them directly from the <Link to={`/property/${b.property_id}`} className="text-[#C85A32] underline">property page</Link> — use the &ldquo;View number&rdquo; or chat button.
            </div>
            <div className="rounded-lg border border-[#2B3A67]/25 bg-white p-4 text-sm" data-testid="pay-broker-block">
              <div className="flex items-center gap-2 font-semibold text-[#2B3A67]"><HandCoins size={16} /> Ready to pay the broker/owner? (optional)</div>
              <p className="mt-1.5 text-[#5C6B7A] text-xs leading-relaxed">
                Once you&rsquo;ve agreed on the token/brokerage with {b.owner_name}, pay it here. The money goes to LocaNest&rsquo;s account, the broker is notified instantly, and we transfer it to them within 3–4 days — commission-free. Never pay a broker directly outside LocaNest.
              </p>
              {prop ? <ProtectedPayButton property={prop} relatedBookingId={b.id} variant="solid" className="mt-3" /> : <div className="mt-3 text-xs text-[#5C6B7A]">Loading payment option…</div>}
            </div>
            <Button
              variant="outline"
              onClick={() => window.confirm("Cancel this interest request? The owner will be able to see it was withdrawn.") && doAction("cancel", { reason: "Tenant withdrew interest" })}
              disabled={busy}
              className="text-red-600 rounded-md"
              data-testid="tenant-cancel"
            >
              <Ban size={14} className="mr-1.5" /> Cancel interest
            </Button>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <div className="text-sm text-[#5C6B7A]">
              Payment not completed yet. <Link to={`/property/${b.property_id}`} className="text-[#C85A32] underline">Retry from the property page</Link>.
            </div>
            <Button
              variant="outline"
              onClick={() => window.confirm("Cancel this pending booking? Since no payment was captured, nothing will be refunded.") && doAction("cancel", { reason: "Tenant cancelled pending booking" })}
              disabled={busy}
              className="text-red-600 rounded-md"
              data-testid="tenant-cancel"
            >
              <Ban size={14} className="mr-1.5" /> Cancel booking
            </Button>
          </div>
        )
      )}

      {(b.status === "completed" || b.status === "advance_paid") && (isTenant || isOwner) && (
        <BookingRatingBlock bookingId={b.id} canRate={b.status === "completed"} onSubmitted={load} />
      )}
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm border-b border-[#1A1F2C]/5 pb-2 last:border-0">
      <span className="text-[#5C6B7A]">{k}</span>
      <span className={mono ? "font-mono text-xs" : ""}>{v}</span>
    </div>
  );
}
